import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/composites/PageHeader';
import { Button } from '@/components/primitives/Button';
import { Select } from '@/components/primitives/Select';
import { EmptyState } from '@/components/primitives/EmptyState';
import { useAppContext } from '@/context/AppContext';
import { useEmployees } from '@/hooks/useEmployees';
import { useDebugSession, type DebugStep, type DebugStepChild } from '@/hooks/useDebugSession';
import { currentDate } from '@/lib/dates';
import { DayDiarySummaryPanel } from '@/components/composites/DayDiarySummaryPanel';
import styles from './DebugDayDiaryPage.module.css';

function todayIso(): string {
  return currentDate().toISOString().slice(0, 10);
}

const KIND_LABEL: Record<string, string> = {
  'fan-out': 'fan-out',
  'text-pass.project': 'text-pass (project)',
  'text-pass.fallback': 'text-pass (fallback)',
  'vision-pass.enrich': 'vision (enrich)',
  'vision-pass.attribute': 'vision (attribute)',
  'consolidate.project': 'consolidate (project)',
  'consolidate.fallback': 'consolidate (reclaim)',
  done: 'done',
};

export function DebugDayDiaryPage() {
  const { currentUser, currentOrg } = useAppContext();
  const navigate = useNavigate();
  const { data: employees, isLoading: employeesLoading } = useEmployees();
  const { session, isStarting, isAdvancing, error, start, next, runVisions, runAll, abort } =
    useDebugSession();

  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedDate, setSelectedDate] = useState(todayIso);
  const [manualStepIndex, setManualStepIndex] = useState<number | null>(null);
  const [manualResponseTab, setManualResponseTab] = useState<'parsed' | 'raw' | 'fanout' | null>(
    null,
  );

  // Admin gate — non-admins are bounced to the dashboard. Mirrors the pattern
  // used by other admin-only pages (no dedicated wrapper component exists yet
  // for org-admin routes).
  useEffect(() => {
    if (currentOrg.id && currentUser.role !== 'admin') {
      navigate('/', { replace: true });
    }
  }, [currentUser.role, currentOrg.id, navigate]);

  const employeeOptions = useMemo(
    () => employees.map((e) => ({ value: e.id, label: e.name })),
    [employees],
  );

  // Selected step index is the last manually-picked one, or the most recent
  // step in the session. Derived rather than stored so the page does not
  // need a setState-in-effect to track the session's growth.
  const selectedStepIndex: number | null =
    session === null
      ? null
      : manualStepIndex !== null && session.steps[manualStepIndex] !== undefined
        ? manualStepIndex
        : session.steps.length > 0
          ? session.steps.length - 1
          : null;

  const selectedStep: DebugStep | null =
    session !== null && selectedStepIndex !== null && session.steps[selectedStepIndex] !== undefined
      ? session.steps[selectedStepIndex]
      : null;

  // Default response tab: Raw when the step has an error or its parsed
  // output is null (parse failed) so the operator sees what the model
  // actually returned without an extra click. Manual selection wins.
  const responseTab: 'parsed' | 'raw' | 'fanout' =
    manualResponseTab !== null
      ? manualResponseTab
      : selectedStep !== null &&
          (selectedStep.error !== null ||
            (selectedStep.response !== null && selectedStep.response.parsed === null))
        ? 'raw'
        : 'parsed';

  const totalTokens = useMemo(() => {
    if (session === null) return { input: 0, output: 0 };
    let input = 0;
    let output = 0;
    for (const s of session.steps) {
      if (s.response !== null) {
        input += s.response.inputTokens;
        output += s.response.outputTokens;
      }
    }
    return { input, output };
  }, [session]);

  const handleStart = () => {
    if (!selectedEmployeeId || !selectedDate) return;
    void start({ employeeId: selectedEmployeeId, date: selectedDate });
  };

  const canStart =
    !!selectedEmployeeId && !!selectedDate && !isStarting && !isAdvancing && session === null;
  const sessionDone = session?.status === 'done';
  const sessionRunning = session?.status === 'running';
  // While background visions are in flight (session.status === 'running')
  // the user cannot click Next / Run all — both would advance the stack
  // while pending visions own reserved slots above the next frame.
  const canAdvance =
    session !== null && !sessionDone && !sessionRunning && !isAdvancing && !isStarting;

  // The "Run all visions" button is meaningful when the next pending frame is
  // a vision frame. The server is the source of truth (it owns the frame
  // stack), but we can give the client a good hint from the last step:
  //   - Text-pass step whose fanOut lists at least one vision child → next is vision.
  //   - Vision-pass step → there are likely sibling visions still pending.
  // When neither holds (last step was consolidate, or fan-out with no visions
  // anywhere in its children), hide the button to avoid no-op clicks. The
  // server still no-ops if our hint is wrong.
  const lastStep =
    session !== null && session.steps.length > 0 ? session.steps[session.steps.length - 1] : null;
  const nextIsLikelyVision =
    lastStep !== null &&
    (lastStep.kind === 'vision-pass.enrich' ||
      lastStep.kind === 'vision-pass.attribute' ||
      lastStep.fanOut.some(
        (c) => c.kind === 'vision-pass.enrich' || c.kind === 'vision-pass.attribute',
      ));
  const canRunVisions = canAdvance && nextIsLikelyVision;

  return (
    <>
      <PageHeader
        title="Day-Diary v2 Step Debugger"
        subtitle="Walk the LLM pipeline one call at a time. Read-only against the org's data."
      />

      <div className={styles.controlBar}>
        <Select
          label="Employee"
          value={selectedEmployeeId}
          options={employeeOptions}
          placeholder={employeesLoading ? 'Loading…' : 'Select employee'}
          onChange={(v) => setSelectedEmployeeId(v)}
          size="md"
          disabled={session !== null}
        />
        <div className={styles.dateField}>
          <label className={styles.dateLabel} htmlFor="debug-diary-date">
            Date
          </label>
          <input
            id="debug-diary-date"
            type="date"
            className={styles.dateInput}
            value={selectedDate}
            max={todayIso()}
            disabled={session !== null}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
        {session === null ? (
          <Button onClick={handleStart} disabled={!canStart} loading={isStarting}>
            Start session
          </Button>
        ) : (
          <>
            <Button onClick={() => void next()} disabled={!canAdvance} loading={isAdvancing}>
              {sessionDone ? 'Session complete' : 'Next step'}
            </Button>
            {nextIsLikelyVision && (
              <Button
                variant="secondary"
                onClick={() => void runVisions()}
                disabled={!canRunVisions}
                loading={isAdvancing}
              >
                Run all visions
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => void runAll()}
              disabled={!canAdvance}
              loading={isAdvancing}
              title="Run every remaining step to completion. Visions in each batch run in parallel; text-pass + consolidate run serially."
            >
              Run all
            </Button>
            <Button variant="secondary" onClick={() => void abort()}>
              Abort
            </Button>
          </>
        )}
      </div>

      {error !== null && (
        <div className={styles.errorBanner} role="alert">
          {error}
        </div>
      )}

      {session === null ? (
        <EmptyState
          title="No active session"
          description="Pick an employee and a date, then click Start session. Each Next click runs one LLM call."
        />
      ) : (
        <div className={styles.body}>
          <div className={styles.stats}>
            <span>
              Session <code>{session.id.slice(0, 8)}…</code>
            </span>
            <span>Steps: {session.steps.length}</span>
            <span>
              Tokens: {totalTokens.input.toLocaleString()} in ·{' '}
              {totalTokens.output.toLocaleString()} out
            </span>
            <span>Status: {session.status}</span>
          </div>

          <div className={styles.split}>
            <aside className={styles.stepList} aria-label="Pipeline steps">
              {session.steps.map((step) => {
                // A "running" placeholder step has no response, no error,
                // and is not one of the no-LLM kinds (`fan-out`/`done`).
                // The runner pre-allocates these when a vision batch fires
                // in the background and replaces them in-place when the
                // underlying LLM call resolves.
                const isRunning =
                  step.response === null &&
                  step.error === null &&
                  step.kind !== 'fan-out' &&
                  step.kind !== 'done';
                return (
                  <button
                    key={step.index}
                    type="button"
                    className={`${styles.stepRow} ${
                      step.index === selectedStepIndex ? styles.stepRowActive : ''
                    } ${step.error ? styles.stepRowError : ''} ${
                      isRunning ? styles.stepRowRunning : ''
                    }`}
                    onClick={() => {
                      setManualStepIndex(step.index);
                      setManualResponseTab(null);
                    }}
                  >
                    <span className={styles.stepIndex}>#{step.index}</span>
                    <span className={styles.stepKind}>{KIND_LABEL[step.kind] ?? step.kind}</span>
                    <span className={styles.stepLabel}>{step.label}</span>
                    {step.response !== null && (
                      <span className={styles.stepTokens} title={`${step.response.elapsedMs} ms`}>
                        {step.response.inputTokens.toLocaleString()} /{' '}
                        {step.response.outputTokens.toLocaleString()}
                      </span>
                    )}
                    {isRunning && <span className={styles.stepSpinner} aria-hidden="true" />}
                  </button>
                );
              })}
            </aside>

            <section className={styles.detail} aria-label="Step detail">
              {selectedStep === null ? (
                <p className={styles.muted}>Select a step to inspect.</p>
              ) : (
                <StepDetail
                  step={selectedStep}
                  tab={responseTab}
                  onTabChange={setManualResponseTab}
                />
              )}
            </section>
          </div>

          <DayDiarySummaryPanel steps={session.steps} />
        </div>
      )}
    </>
  );
}

interface StepDetailProps {
  step: DebugStep;
  tab: 'parsed' | 'raw' | 'fanout';
  onTabChange: (tab: 'parsed' | 'raw' | 'fanout') => void;
}

function StepDetail({ step, tab, onTabChange }: StepDetailProps) {
  return (
    <div className={styles.stepDetail}>
      <header className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>{step.label}</h2>
        <span className={styles.stepKindBadge}>{KIND_LABEL[step.kind] ?? step.kind}</span>
        {step.response !== null && (
          <span className={styles.muted}>
            {step.response.elapsedMs} ms · {step.response.inputTokens} in /{' '}
            {step.response.outputTokens} out
          </span>
        )}
      </header>

      {step.error !== null && (
        <div className={styles.errorBanner} role="alert">
          <strong>Error:</strong> {step.error.message}
        </div>
      )}

      {step.prompt !== null && (
        <section className={styles.promptPanel}>
          <div className={styles.panelHeader}>
            <h3>Prompt</h3>
            <span className={styles.muted}>{step.prompt.model}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (step.prompt) void navigator.clipboard.writeText(promptForCopy(step.prompt));
              }}
            >
              Copy
            </Button>
          </div>
          {step.prompt.system && (
            <>
              <div className={styles.subLabel}>system</div>
              <pre className={styles.pre}>{step.prompt.system}</pre>
            </>
          )}
          <div className={styles.subLabel}>user</div>
          <pre className={styles.pre}>{step.prompt.user}</pre>
        </section>
      )}

      {(step.response !== null || step.fanOut.length > 0) && (
        <section className={styles.responsePanel}>
          <div className={styles.panelHeader}>
            <h3>Response</h3>
            <div className={styles.tabs} role="tablist">
              <button
                role="tab"
                aria-selected={tab === 'parsed'}
                className={tab === 'parsed' ? styles.tabActive : styles.tab}
                onClick={() => onTabChange('parsed')}
              >
                Parsed
              </button>
              <button
                role="tab"
                aria-selected={tab === 'raw'}
                className={tab === 'raw' ? styles.tabActive : styles.tab}
                onClick={() => onTabChange('raw')}
              >
                Raw
              </button>
              <button
                role="tab"
                aria-selected={tab === 'fanout'}
                className={tab === 'fanout' ? styles.tabActive : styles.tab}
                onClick={() => onTabChange('fanout')}
              >
                Fan-out ({step.fanOut.length})
              </button>
            </div>
            {step.response !== null && <CopyResponseButton step={step} tab={tab} />}
          </div>
          {tab === 'parsed' && (
            <pre className={styles.pre}>
              {step.response === null
                ? '(no response)'
                : JSON.stringify(step.response.parsed, null, 2)}
            </pre>
          )}
          {tab === 'raw' && (
            <pre className={styles.pre}>
              {step.response === null ? '(no response)' : step.response.raw}
            </pre>
          )}
          {tab === 'fanout' && <FanOutList children={step.fanOut} />}
        </section>
      )}
    </div>
  );
}

interface CopyResponseButtonProps {
  step: DebugStep;
  tab: 'parsed' | 'raw' | 'fanout';
}

function CopyResponseButton({ step, tab }: CopyResponseButtonProps) {
  const onClick = () => {
    const text = textForCopy(step, tab);
    if (text !== null) void navigator.clipboard.writeText(text);
  };
  return (
    <Button variant="ghost" size="sm" onClick={onClick}>
      Copy
    </Button>
  );
}

function textForCopy(step: DebugStep, tab: 'parsed' | 'raw' | 'fanout'): string | null {
  if (tab === 'fanout') return JSON.stringify(step.fanOut, null, 2);
  if (step.response === null) return null;
  return tab === 'raw' ? step.response.raw : JSON.stringify(step.response.parsed, null, 2);
}

function FanOutList({ children }: { children: DebugStepChild[] }) {
  if (children.length === 0) {
    return <p className={styles.muted}>(no child steps)</p>;
  }
  return (
    <ul className={styles.fanOutList}>
      {children.map((c, i) => (
        <li key={i}>
          <span className={styles.stepKindBadge}>{KIND_LABEL[c.kind] ?? c.kind}</span>
          <span>{c.label}</span>
        </li>
      ))}
    </ul>
  );
}

function promptForCopy(prompt: { system: string | null; user: string }): string {
  if (prompt.system) {
    return `# SYSTEM\n${prompt.system}\n\n# USER\n${prompt.user}`;
  }
  return prompt.user;
}
